import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import * as XLSX from 'xlsx'
import { supabase } from '../supabaseClient'
import ConfirmModal from './ConfirmModal'
import { USER_ROLES } from '../config/security'
import './EngenhariaHomologacao.css'

/** Administrativo desta empresa pode usar o filtro por empresa (mesma regra de escopo ampliado). */
const EMPRESA_ID_ADMINISTRATIVO_PODE_FILTRAR = 'f0c6de5a-eaf9-4530-a6a7-28140091897d'

const TIPOS_PROCESSO = [
  { value: 'parecer_acesso', label: 'Parecer de acesso', prazo: 15, modoContagem: 'calendario' },
  { value: 'troca_titularidade', label: 'Troca de Titularidade', prazo: 15, modoContagem: 'calendario' },
  { value: 'vistoria_conexao', label: 'Vistoria de Conexão', prazo: 5, modoContagem: 'dias_uteis' },
  { value: 'aumento_carga', label: 'Aumento de Carga', prazo: 5, modoContagem: 'dias_uteis' }
]

const STATUS_PROCESSO = [
  { value: 'solicitado', label: 'Solicitado' },
  { value: 'aprovado', label: 'Aprovado' },
  { value: 'atendido', label: 'Atendido' },
  { value: 'reprovado', label: 'Reprovado' }
]

function formatarProcessoParaExcel(item) {
  if (!item) return '—'
  const st = STATUS_PROCESSO.find((s) => s.value === item.status)?.label || item.status
  const ps = parseToDigits(item.protocolo_site) || '—'
  const pr = parseToDigits(item.protocolo_solicitacao) || '—'
  return `${st} | Site: ${ps} | Solicitação: ${pr}`
}

function parseToDigits(value) {
  return String(value ?? '').replace(/\D/g, '')
}

function formatDateTime(dateValue) {
  if (!dateValue) return '—'
  const d = typeof dateValue === 'string' ? new Date(dateValue) : dateValue
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function isoToDatetimeLocalValue(dateValue) {
  if (!dateValue) return ''
  const d = typeof dateValue === 'string' ? new Date(dateValue) : dateValue
  if (Number.isNaN(d.getTime())) return ''

  // datetime-local espera: YYYY-MM-DDTHH:mm (sem timezone)
  const pad = (n) => String(n).padStart(2, '0')
  const yyyy = d.getFullYear()
  const mm = pad(d.getMonth() + 1)
  const dd = pad(d.getDate())
  const hh = pad(d.getHours())
  const min = pad(d.getMinutes())
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`
}

function nowDatetimeLocalValue() {
  return isoToDatetimeLocalValue(new Date())
}

function countCalendarDaysElapsed(fromValue, toValue) {
  const from = new Date(fromValue)
  const to = new Date(toValue)
  from.setHours(0, 0, 0, 0)
  to.setHours(0, 0, 0, 0)
  const diffMs = to.getTime() - from.getTime()
  if (diffMs <= 0) return 0
  return Math.floor(diffMs / (1000 * 60 * 60 * 24))
}

/** Texto do tooltip nativo quando o processo está atendido (somente leitura na matriz). */
function montarTooltipProcessoHomologacao(item) {
  if (!item) return ''
  const st = STATUS_PROCESSO.find(s => s.value === item.status)?.label || item.status
  const parts = [
    `Status: ${st}`,
    item.data_solicitacao ? `Solicitação: ${formatDateTime(item.data_solicitacao)}` : null,
    item.data_status ? `Data do status: ${formatDateTime(item.data_status)}` : null,
    `Protocolo site: ${parseToDigits(item.protocolo_site) || '—'}`,
    `Protocolo solicitação: ${parseToDigits(item.protocolo_solicitacao) || '—'}`,
    item.processado && item.data_processado ? `Processado: ${formatDateTime(item.data_processado)}` : item.processado ? 'Processado: sim' : null
  ].filter(Boolean)
  return parts.join(' · ')
}

const ARIA_MATRIX = {
  aumento_carga: { abrir: 'Ver aumento de carga', cad: 'Cadastrar aumento de carga' },
  troca_titularidade: { abrir: 'Ver troca de titularidade', cad: 'Cadastrar troca de titularidade' },
  parecer_acesso: { abrir: 'Ver parecer de acesso', cad: 'Cadastrar parecer de acesso' },
  vistoria_conexao: { abrir: 'Ver vistoria', cad: 'Cadastrar vistoria' }
}

function MatrixProcessoCheckboxCell({ cliente, tipoProcesso, item, onOpen }) {
  const atendido = item?.status === 'atendido'
  if (item && atendido) {
    const tip = montarTooltipProcessoHomologacao(item)
    return (
      <td className="text-center align-middle">
        <span
          className="engenharia-homologacao-matrix-cb engenharia-homologacao-matrix-cb--atendido"
          title={tip}
          role="img"
          aria-label="Processo atendido. Passe o cursor para ver os detalhes."
        >
          <input
            type="checkbox"
            className="form-check-input engenharia-homologacao-matrix-cb-input engenharia-homologacao-matrix-cb-input--atendido"
            checked
            disabled
            tabIndex={-1}
            aria-hidden
          />
        </span>
      </td>
    )
  }

  const aria = ARIA_MATRIX[tipoProcesso] || { abrir: 'Ver detalhes', cad: 'Cadastrar processo' }

  return (
    <td className="text-center align-middle">
      <button
        type="button"
        className="engenharia-homologacao-matrix-cb"
        onClick={() => onOpen(cliente, tipoProcesso, item)}
        aria-label={item ? aria.abrir : aria.cad}
        title={item ? 'Ver detalhes' : 'Cadastrar processo'}
      >
        <input
          type="checkbox"
          className="form-check-input engenharia-homologacao-matrix-cb-input"
          checked={!!item}
          onChange={() => {}}
          tabIndex={-1}
          aria-hidden
        />
      </button>
    </td>
  )
}

export default function EngenhariaHomologacao({ onBack, userRole, userEmpresa, userId }) {
  const empresaId = userEmpresa?.id || userEmpresa
  const isAdmin = userRole === 'administrador'

  const eidUsuario = useMemo(
    () => (empresaId != null && empresaId !== '' ? String(empresaId) : ''),
    [empresaId]
  )

  const podeEscolherEmpresaFiltro = useMemo(() => {
    if (userRole === USER_ROLES.ADMINISTRADOR) return true
    if (userRole === USER_ROLES.ADMINISTRATIVO && eidUsuario === EMPRESA_ID_ADMINISTRATIVO_PODE_FILTRAR) return true
    return false
  }, [userRole, eidUsuario])

  const [lista, setLista] = useState([])
  const [loading, setLoading] = useState(false)

  // Filtros da tela
  const [clienteSearch, setClienteSearch] = useState('')
  const [tipoFilter, setTipoFilter] = useState('')
  const [protocoloFilter, setProtocoloFilter] = useState('')
  /** false = não homologados | true = homologados (clientes_cadastro.homologado) */
  const [filtroHomologadosSim, setFiltroHomologadosSim] = useState(false)
  /** Só aplicado quando `podeEscolherEmpresaFiltro`; vazio = todas as empresas */
  const [filtroEmpresaId, setFiltroEmpresaId] = useState('')
  const [listaEmpresasFiltro, setListaEmpresasFiltro] = useState([])
  /** Ordenação por coluna Dias em processo: desc = ▲ mais antigo→novo; asc = ▼ mais novo→antigo */
  const [ordenacaoDiasColuna, setOrdenacaoDiasColuna] = useState('desc')

  // Modal de cadastro
  const [showNovoModal, setShowNovoModal] = useState(false)
  const [saving, setSaving] = useState(false)

  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [statusModalItem, setStatusModalItem] = useState(null)
  const [novoStatus, setNovoStatus] = useState('solicitado')
  const [statusMotivo, setStatusMotivo] = useState('')
  const [dataStatusInput, setDataStatusInput] = useState('')
  const [savingStatus, setSavingStatus] = useState(false)

  const [processadoModalOpen, setProcessadoModalOpen] = useState(false)
  const [processadoModalItem, setProcessadoModalItem] = useState(null)
  const [processadoProtocoloSolicitacao, setProcessadoProtocoloSolicitacao] = useState('')
  const [processadoDataInput, setProcessadoDataInput] = useState('')
  const [savingProcessado, setSavingProcessado] = useState(false)

  const [clientes, setClientes] = useState([])
  const [clienteModalSearch, setClienteModalSearch] = useState('')
  const [showClienteDropdown, setShowClienteDropdown] = useState(false)

  const [formData, setFormData] = useState({
    id_cliente: '',
    tipo_processo: 'parecer_acesso',
    protocolo_site: '',
    protocolo_solicitacao: ''
  })

  /** Modal ao clicar no checkbox da matriz: ver detalhes ou cadastrar processo */
  const [detalheModal, setDetalheModal] = useState(null)
  const [detalheProtoSite, setDetalheProtoSite] = useState('')
  const [detalheProtoSolic, setDetalheProtoSolic] = useState('')
  const [savingDetalhe, setSavingDetalhe] = useState(false)

  const [homologadoModalCliente, setHomologadoModalCliente] = useState(null)
  const [homologadoDraft, setHomologadoDraft] = useState(false)
  const [savingHomologado, setSavingHomologado] = useState(false)

  // Mensagens
  const [msgModal, setMsgModal] = useState({ show: false, title: 'Info', text: '', type: 'success' })
  const showMsg = (text, type = 'success') => {
    setMsgModal({ show: true, title: type === 'error' ? 'Erro' : type === 'warning' ? 'Atenção' : 'Sucesso', text, type })
  }

  const closeMsgModal = () => setMsgModal({ show: false, title: 'Info', text: '', type: 'success' })

  const loadClientes = useCallback(async () => {
    try {
      let q = supabase
        .from('clientes_cadastro')
        .select('id, nome_completo, cpf, empresa_id, data_cadastro, homologado, data_homologado')
        .order('nome_completo')
      if (!isAdmin && empresaId && !podeEscolherEmpresaFiltro) q = q.eq('empresa_id', empresaId)
      const { data, error } = await q
      if (error) throw error
      setClientes(data || [])
    } catch (e) {
      console.error(e)
      showMsg('Erro ao carregar clientes: ' + e.message, 'error')
    }
  }, [empresaId, isAdmin, podeEscolherEmpresaFiltro])

  const loadLista = useCallback(async () => {
    try {
      setLoading(true)

      let q = supabase
        .from('engenharia_homologacao')
        .select(`
          id,
          id_cliente,
          tipo_processo,
          data_solicitacao,
          processado,
          data_processado,
          id_responsavel,
          protocolo_site,
          protocolo_solicitacao,
          status,
          data_status,
          status_motivo,
          cliente:clientes_cadastro(id, nome_completo, cpf, empresa_id, data_cadastro, homologado, data_homologado)
        `)
        .order('data_solicitacao', { ascending: false })

      if (!isAdmin && empresaId && !podeEscolherEmpresaFiltro) {
        const { data: clientesData, error: clientesErr } = await supabase
          .from('clientes_cadastro')
          .select('id')
          .eq('empresa_id', empresaId)

        if (clientesErr) throw clientesErr

        const ids = (clientesData || []).map(c => c.id)
        if (ids.length === 0) {
          setLista([])
          return
        }
        q = q.in('id_cliente', ids)
      }

      const { data, error } = await q
      if (error) throw error

      setLista(data || [])
    } catch (e) {
      console.error(e)
      showMsg('Erro ao carregar registros: ' + e.message, 'error')
    } finally {
      setLoading(false)
    }
  }, [empresaId, isAdmin, podeEscolherEmpresaFiltro])

  useEffect(() => {
    loadClientes()
    loadLista()
  }, [loadClientes, loadLista])

  useEffect(() => {
    if (!podeEscolherEmpresaFiltro) {
      setFiltroEmpresaId('')
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const { data, error } = await supabase
          .from('empresa')
          .select('id, empresa, razao_social')
          .order('empresa')
        if (error) throw error
        if (!cancelled) setListaEmpresasFiltro(data || [])
      } catch (e) {
        console.error(e)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [podeEscolherEmpresaFiltro])

  const handleAtualizar = useCallback(async () => {
    await loadLista()
    await loadClientes()
  }, [loadLista, loadClientes])

  const tiposMap = useMemo(() => {
    const m = {}
    for (const t of TIPOS_PROCESSO) m[t.value] = t
    return m
  }, [])

  const clientesFiltradosModal = useMemo(() => {
    const s = clienteModalSearch.trim().toLowerCase()
    if (!s) return clientes.slice(0, 15)

    const digits = parseToDigits(s)
    return clientes.filter(c => {
      const nome = c.nome_completo?.toLowerCase() || ''
      const cpfDigits = parseToDigits(c.cpf)
      return nome.includes(s) || (digits && cpfDigits.includes(digits))
    }).slice(0, 15)
  }, [clientes, clienteModalSearch])

  const listaFiltrada = useMemo(() => {
    const clienteBusca = clienteSearch.trim().toLowerCase()
    const clienteBuscaDigits = parseToDigits(clienteSearch)
    const protocoloBusca = parseToDigits(protocoloFilter)
    const tipo = tipoFilter

    return lista.filter(item => {
      if (tipo && item.tipo_processo !== tipo) return false

      if (clienteBusca) {
        const nome = item.cliente?.nome_completo?.toLowerCase() || ''
        const cpfDigits = parseToDigits(item.cliente?.cpf)
        const okNome = nome.includes(clienteBusca)
        const okCpf = clienteBuscaDigits && cpfDigits.includes(clienteBuscaDigits)
        if (!okNome && !okCpf) return false
      }

      if (protocoloBusca) {
        const pSite = parseToDigits(item.protocolo_site)
        const pSolic = parseToDigits(item.protocolo_solicitacao)
        if (!pSite.includes(protocoloBusca) && !pSolic.includes(protocoloBusca)) return false
      }

      if (podeEscolherEmpresaFiltro && filtroEmpresaId) {
        if (String(item.cliente?.empresa_id ?? '') !== String(filtroEmpresaId)) return false
      }

      return true
    })
  }, [clienteSearch, lista, protocoloFilter, tipoFilter, podeEscolherEmpresaFiltro, filtroEmpresaId])

  /** Clientes que passam nos filtros atuais (usado para montar linhas; células usam todos os tipos do cliente em `lista`). */
  const idsClientesFiltrados = useMemo(() => {
    const s = new Set()
    for (const item of listaFiltrada) {
      if (item?.id_cliente) s.add(item.id_cliente)
    }
    return s
  }, [listaFiltrada])

  const pickLatestByTipo = useCallback((items) => {
    const out = {
      aumento_carga: null,
      troca_titularidade: null,
      parecer_acesso: null,
      vistoria_conexao: null
    }
    for (const it of items) {
      const k = it.tipo_processo
      if (!Object.prototype.hasOwnProperty.call(out, k)) continue
      if (!out[k] || new Date(it.data_solicitacao) > new Date(out[k].data_solicitacao)) {
        out[k] = it
      }
    }
    return out
  }, [])

  const linhasTabela = useMemo(() => {
    const porCliente = new Map()
    for (const item of lista) {
      const cid = item?.id_cliente
      if (!cid || !idsClientesFiltrados.has(cid)) continue
      if (!porCliente.has(cid)) porCliente.set(cid, [])
      porCliente.get(cid).push(item)
    }

    const rows = []
    for (const [, items] of porCliente.entries()) {
      const cliente = items[0]?.cliente || null
      rows.push({
        clienteId: items[0].id_cliente,
        cliente,
        porTipo: pickLatestByTipo(items)
      })
    }
    rows.sort((a, b) =>
      (a.cliente?.nome_completo || '').localeCompare(b.cliente?.nome_completo || '', 'pt-BR', { sensitivity: 'base' })
    )
    return rows
  }, [lista, idsClientesFiltrados, pickLatestByTipo])

  const diasDesdeCadastroCliente = useCallback((dataCadastro) => {
    if (!dataCadastro) return null
    return countCalendarDaysElapsed(dataCadastro, new Date())
  }, [])

  const linhasTabelaOrdenadas = useMemo(() => {
    let rows = [...linhasTabela]
    rows = rows.filter((r) =>
      filtroHomologadosSim ? r.cliente?.homologado === true : !r.cliente?.homologado
    )
    const mult = ordenacaoDiasColuna === 'asc' ? 1 : -1
    rows.sort((a, b) => {
      const da = diasDesdeCadastroCliente(a.cliente?.data_cadastro)
      const db = diasDesdeCadastroCliente(b.cliente?.data_cadastro)
      if (da == null && db == null) {
        return (a.cliente?.nome_completo || '').localeCompare(b.cliente?.nome_completo || '', 'pt-BR', { sensitivity: 'base' })
      }
      if (da == null) return 1
      if (db == null) return -1
      if (da !== db) return mult * (da - db)
      return (a.cliente?.nome_completo || '').localeCompare(b.cliente?.nome_completo || '', 'pt-BR', { sensitivity: 'base' })
    })
    return rows
  }, [linhasTabela, filtroHomologadosSim, ordenacaoDiasColuna, diasDesdeCadastroCliente])

  const nomeEmpresaPorId = useMemo(() => {
    const m = new Map()
    for (const e of listaEmpresasFiltro) {
      if (e?.id != null) m.set(String(e.id), e.empresa || e.razao_social || '')
    }
    if (userEmpresa?.id != null) {
      m.set(String(userEmpresa.id), userEmpresa.empresa || userEmpresa.razao_social || '')
    }
    return m
  }, [listaEmpresasFiltro, userEmpresa])

  const exportarParaExcel = useCallback(() => {
    const headers = [
      'Nome do cliente',
      'CPF',
      'Empresa',
      'Homologado',
      'Data homologação',
      'Dias em processo',
      'Data cadastro (cliente)',
      'Aumento de carga',
      'Troca de titularidade',
      'Parecer de acesso',
      'Vistoria de conexão'
    ]
    const rows = linhasTabelaOrdenadas.map((row) => {
      const { cliente, porTipo } = row
      const eid = cliente?.empresa_id != null ? String(cliente.empresa_id) : ''
      const nomeEmp = (eid && nomeEmpresaPorId.get(eid)) || eid || '—'
      const dc = cliente?.data_cadastro
      const diasProc = diasDesdeCadastroCliente(dc)
      return [
        cliente?.nome_completo ?? '',
        parseToDigits(cliente?.cpf) || '',
        nomeEmp,
        cliente?.homologado ? 'Sim' : 'Não',
        cliente?.data_homologado ? formatDateTime(cliente.data_homologado) : '—',
        diasProc == null ? '—' : diasProc,
        dc ? formatDateTime(dc) : '—',
        formatarProcessoParaExcel(porTipo.aumento_carga),
        formatarProcessoParaExcel(porTipo.troca_titularidade),
        formatarProcessoParaExcel(porTipo.parecer_acesso),
        formatarProcessoParaExcel(porTipo.vistoria_conexao)
      ]
    })
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Homologação')
    const nomeArquivo = `homologacao_${new Date().toISOString().slice(0, 10)}.xlsx`
    XLSX.writeFile(wb, nomeArquivo)
  }, [linhasTabelaOrdenadas, nomeEmpresaPorId, diasDesdeCadastroCliente])

  const alternarOrdenacaoDias = useCallback(() => {
    setOrdenacaoDiasColuna((prev) => (prev === 'desc' ? 'asc' : 'desc'))
  }, [])

  const statusBadgeClass = useCallback((statusValue) => {
    if (statusValue === 'atendido') return 'engenharia-homologacao-status-badge engenharia-homologacao-status-atendido'
    if (statusValue === 'aprovado') return 'engenharia-homologacao-status-badge engenharia-homologacao-status-aprovado'
    if (statusValue === 'reprovado') return 'engenharia-homologacao-status-badge engenharia-homologacao-status-reprovado'
    return 'engenharia-homologacao-status-badge engenharia-homologacao-status-solicitado'
  }, [])

  const resetModal = () => {
    setFormData({
      id_cliente: '',
      tipo_processo: 'parecer_acesso',
      protocolo_site: '',
      protocolo_solicitacao: ''
    })
    setClienteModalSearch('')
    setShowClienteDropdown(false)
  }

  const fecharDetalheModal = useCallback(() => {
    setDetalheModal(null)
    setDetalheProtoSite('')
    setDetalheProtoSolic('')
    setSavingDetalhe(false)
  }, [])

  const abrirDetalheProcesso = useCallback((cliente, tipoProcesso, item) => {
    setDetalheProtoSite('')
    setDetalheProtoSolic('')
    setDetalheModal({ cliente, tipoProcesso, item })
  }, [])

  const abrirModalHomologadoCliente = useCallback((cliente) => {
    if (!cliente?.id) return
    setHomologadoModalCliente(cliente)
    setHomologadoDraft(!!cliente.homologado)
  }, [])

  const fecharModalHomologadoCliente = useCallback(() => {
    setHomologadoModalCliente(null)
    setSavingHomologado(false)
  }, [])

  const salvarHomologadoCliente = useCallback(async () => {
    if (!homologadoModalCliente?.id) return
    const homologado = homologadoDraft
    try {
      setSavingHomologado(true)
      const { error } = await supabase
        .from('clientes_cadastro')
        .update({
          homologado,
          data_homologado: homologado ? new Date().toISOString() : null
        })
        .eq('id', homologadoModalCliente.id)
      if (error) throw error
      fecharModalHomologadoCliente()
      await loadLista()
      await loadClientes()
      showMsg('Homologação do cliente atualizada.')
    } catch (e) {
      console.error(e)
      showMsg('Erro ao salvar homologação: ' + e.message, 'error')
    } finally {
      setSavingHomologado(false)
    }
  }, [homologadoModalCliente, homologadoDraft, fecharModalHomologadoCliente, loadLista, loadClientes])

  const handleCriarDesdeDetalhe = async () => {
    if (!detalheModal?.cliente?.id || !detalheModal?.tipoProcesso) return
    const idCliente = detalheModal.cliente.id
    const tipo = detalheModal.tipoProcesso
    const protocoloSiteDigits = parseToDigits(detalheProtoSite)
    const protocoloSolicDigits = parseToDigits(detalheProtoSolic)

    if (!tipo || !tiposMap[tipo]) {
      showMsg('Tipo de processo inválido.', 'error')
      return
    }

    try {
      setSavingDetalhe(true)
      const payload = {
        id_cliente: idCliente,
        tipo_processo: tipo,
        id_responsavel: userId || null,
        status: 'solicitado',
        protocolo_site: protocoloSiteDigits ? protocoloSiteDigits : null,
        protocolo_solicitacao: protocoloSolicDigits ? protocoloSolicDigits : null,
        processado: false
      }
      const { error } = await supabase.from('engenharia_homologacao').insert([payload])
      if (error) throw error
      fecharDetalheModal()
      await loadLista()
      showMsg('Registro cadastrado com sucesso.')
    } catch (e) {
      console.error(e)
      showMsg('Erro ao cadastrar: ' + e.message, 'error')
    } finally {
      setSavingDetalhe(false)
    }
  }

  const handleCriar = async () => {
    const idCliente = formData.id_cliente
    const tipo = formData.tipo_processo
    const protocoloSiteDigits = parseToDigits(formData.protocolo_site)
    const protocoloSolicDigits = parseToDigits(formData.protocolo_solicitacao)

    if (!idCliente) {
      showMsg('Selecione o cliente.', 'error')
      return
    }
    if (!tipo || !tiposMap[tipo]) {
      showMsg('Selecione o tipo de processo.', 'error')
      return
    }

    try {
      setSaving(true)

      const payload = {
        id_cliente: idCliente,
        tipo_processo: tipo,
        id_responsavel: userId || null,
        status: 'solicitado',
        // Campos opcionais: se vazio, salva como NULL
        protocolo_site: protocoloSiteDigits ? protocoloSiteDigits : null,
        protocolo_solicitacao: protocoloSolicDigits ? protocoloSolicDigits : null,
        processado: false
      }

      const { error } = await supabase.from('engenharia_homologacao').insert([payload])
      if (error) throw error

      resetModal()
      setShowNovoModal(false)
      await loadLista()
      showMsg('Registro cadastrado com sucesso.')
    } catch (e) {
      console.error(e)
      showMsg('Erro ao cadastrar: ' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleSetProcessado = async (item, value) => {
    try {
      setLoading(true)
      const { error } = await supabase
        .from('engenharia_homologacao')
        .update({
          processado: value,
          data_processado: value ? item?.data_processado || null : null
        })
        .eq('id', item.id)

      if (error) throw error
      await loadLista()
      fecharDetalheModal()
      showMsg(value ? 'Processo marcado como processado.' : 'Processo revertido (processado = falso).', value ? 'success' : 'warning')
    } catch (e) {
      console.error(e)
      showMsg('Erro ao atualizar processado: ' + e.message, 'error')
    } finally {
      setLoading(false)
    }
  }

  const abrirModalStatus = (item) => {
    setStatusModalItem(item)
    setNovoStatus(item?.status || 'solicitado')
    setStatusMotivo(item?.status_motivo || '')
    // Deve abrir já preenchido com data/hora atual
    setDataStatusInput(nowDatetimeLocalValue())
    setStatusModalOpen(true)
  }

  const closeModalStatus = () => {
    setStatusModalOpen(false)
    setStatusModalItem(null)
    setSavingStatus(false)
    setDataStatusInput('')
  }

  const abrirModalProcessado = (item) => {
    fecharDetalheModal()
    setProcessadoModalItem(item)
    setProcessadoProtocoloSolicitacao(parseToDigits(item?.protocolo_solicitacao) || '')
    setProcessadoDataInput(nowDatetimeLocalValue())
    setProcessadoModalOpen(true)
  }

  const closeModalProcessado = () => {
    setProcessadoModalOpen(false)
    setProcessadoModalItem(null)
    setProcessadoProtocoloSolicitacao('')
    setProcessadoDataInput('')
    setSavingProcessado(false)
  }

  const handleSalvarStatus = async () => {
    if (!statusModalItem?.id) return

    const motivoTrim = statusMotivo.trim()
    const precisaMotivo = novoStatus === 'reprovado'

    if (precisaMotivo && !motivoTrim) {
      showMsg('Informe o motivo para Cancelado.', 'error')
      return
    }

    // Validação leve: se o input de datetime estiver preenchido, precisa ser uma data válida
    if (dataStatusInput) {
      const d = new Date(dataStatusInput)
      if (Number.isNaN(d.getTime())) {
        showMsg('Data do status inválida.', 'error')
        return
      }
    }

    try {
      setSavingStatus(true)

      const { error } = await supabase
        .from('engenharia_homologacao')
        .update({
          status: novoStatus,
          // O usuário controla quando preencher a data_status
          data_status: dataStatusInput ? new Date(dataStatusInput).toISOString() : null,
          status_motivo: precisaMotivo ? (motivoTrim || null) : null
        })
        .eq('id', statusModalItem.id)

      if (error) throw error

      await loadLista()
      showMsg('Status atualizado com sucesso.')
      closeModalStatus()
    } catch (e) {
      console.error(e)
      showMsg('Erro ao atualizar status: ' + e.message, 'error')
      setSavingStatus(false)
    }
  }

  const handleConfirmarProcessado = async () => {
    if (!processadoModalItem?.id) return

    const digits = parseToDigits(processadoProtocoloSolicitacao)
    if (!digits) {
      showMsg('Informe o Protocolo da Solicitação (apenas números).', 'error')
      return
    }
    if (!processadoDataInput) {
      showMsg('Informe a data e hora do processamento.', 'error')
      return
    }

    const dataProcessado = new Date(processadoDataInput)
    if (Number.isNaN(dataProcessado.getTime())) {
      showMsg('Data/hora do processamento inválida.', 'error')
      return
    }

    try {
      setSavingProcessado(true)

      const { error } = await supabase
        .from('engenharia_homologacao')
        .update({
          protocolo_solicitacao: digits,
          processado: true,
          data_processado: dataProcessado.toISOString()
        })
        .eq('id', processadoModalItem.id)

      if (error) throw error

      await loadLista()
      showMsg('Processo marcado como processado.')
      closeModalProcessado()
    } catch (e) {
      console.error(e)
      showMsg('Erro ao marcar processado: ' + e.message, 'error')
      setSavingProcessado(false)
    }
  }

  const modalContent = showNovoModal ? (
    createPortal(
      <div
        className="engenharia-homologacao-modal-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowNovoModal(false)
            resetModal()
          }
        }}
      >
        <div className="engenharia-homologacao-modal" onClick={(e) => e.stopPropagation()}>
          <div className="engenharia-homologacao-modal-header">
            <div>
              <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>Novo registro</h3>
              <p style={{ margin: '6px 0 0', opacity: 0.9, fontSize: 13 }}>Cadastro pelo botão abre este modal</p>
            </div>
            <button
              type="button"
              className="engenharia-homologacao-modal-close"
              onClick={() => {
                setShowNovoModal(false)
                resetModal()
              }}
              title="Fechar"
            >
              ×
            </button>
          </div>

          <div className="engenharia-homologacao-form">
            <div style={{ marginBottom: 16 }}>
              <div className="engenharia-homologacao-field" style={{ position: 'relative' }}>
                <label>Nome do cliente (nome ou CPF)</label>
                <input
                  type="text"
                  value={clienteModalSearch}
                  placeholder="Buscar..."
                  onChange={(e) => {
                    setClienteModalSearch(e.target.value)
                    setShowClienteDropdown(true)
                  }}
                  onFocus={() => setShowClienteDropdown(true)}
                />

                {showClienteDropdown && clientesFiltradosModal.length > 0 && (
                  <div className="engenharia-homologacao-dropdown">
                    {clientesFiltradosModal.map(c => (
                      <div
                        key={c.id}
                        className="engenharia-homologacao-dropdown-item"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, id_cliente: c.id }))
                          setClienteModalSearch(c.nome_completo)
                          setShowClienteDropdown(false)
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>{c.nome_completo}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>CPF: {parseToDigits(c.cpf)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="engenharia-homologacao-field">
              <label>Tipo do Processo</label>
              <select
                value={formData.tipo_processo}
                onChange={(e) => setFormData(prev => ({ ...prev, tipo_processo: e.target.value }))}
              >
                {TIPOS_PROCESSO.map(t => (
                  <option value={t.value} key={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="engenharia-homologacao-field">
              <label>Protocolo do Site (apenas números, opcional)</label>
              <input
                inputMode="numeric"
                value={formData.protocolo_site}
                onChange={(e) => setFormData(prev => ({ ...prev, protocolo_site: parseToDigits(e.target.value) }))}
                placeholder="Ex.: 123456"
              />
            </div>

            <div className="engenharia-homologacao-field">
              <label>Protocolo da Solicitação (apenas números, opcional)</label>
              <input
                inputMode="numeric"
                value={formData.protocolo_solicitacao}
                onChange={(e) => setFormData(prev => ({ ...prev, protocolo_solicitacao: parseToDigits(e.target.value) }))}
                placeholder="Ex.: 987654"
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 10 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowNovoModal(false)
                  resetModal()
                }}
                disabled={saving}
              >
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={handleCriar} disabled={saving}>
                {saving ? 'Salvando...' : 'Cadastrar'}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    )
  ) : null

  const statusModalContent = statusModalOpen && statusModalItem ? (
    createPortal(
      <div
        className="engenharia-homologacao-modal-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) closeModalStatus()
        }}
        style={{ zIndex: 1000002 }}
      >
        <div className="engenharia-homologacao-modal" onClick={(e) => e.stopPropagation()}>
          <div className="engenharia-homologacao-modal-header">
            <div>
              <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>Alterar status</h3>
              <p style={{ margin: '6px 0 0', opacity: 0.9, fontSize: 13 }}>
                Cliente: {statusModalItem?.cliente?.nome_completo || '—'}
              </p>
              <p style={{ margin: '4px 0 0', opacity: 0.95, fontSize: 13, fontWeight: 600 }}>
                Processo: {tiposMap[statusModalItem?.tipo_processo]?.label || statusModalItem?.tipo_processo || '—'}
              </p>
              <p style={{ margin: '2px 0 0', opacity: 0.85, fontSize: 12 }}>
                Solicitado em: {statusModalItem?.data_solicitacao ? formatDateTime(statusModalItem.data_solicitacao) : '—'}
              </p>
            </div>
            <button
              type="button"
              className="engenharia-homologacao-modal-close"
              onClick={closeModalStatus}
              title="Fechar"
            >
              ×
            </button>
          </div>

          <div className="engenharia-homologacao-form">
            <div className="engenharia-homologacao-field">
              <label>Status</label>
              <select value={novoStatus} onChange={(e) => setNovoStatus(e.target.value)}>
                {STATUS_PROCESSO.map(s => (
                  <option value={s.value} key={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            {novoStatus === 'reprovado' && (
              <div className="engenharia-homologacao-field">
                <label>Motivo (status_motivo)</label>
                <textarea
                  rows={4}
                  value={statusMotivo}
                  onChange={(e) => setStatusMotivo(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                  placeholder="Descreva o motivo..."
                />
              </div>
            )}

            <div className="engenharia-homologacao-field">
              <label>Data do status (data_status)</label>
              <input
                type="datetime-local"
                value={dataStatusInput}
                onChange={(e) => setDataStatusInput(e.target.value)}
              />
              <small style={{ fontSize: 12, color: '#6b7280' }}>
                Deixe em branco para manter sem data.
              </small>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 10 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeModalStatus}
                disabled={savingStatus}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSalvarStatus}
                disabled={savingStatus}
              >
                {savingStatus ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    )
  ) : null

  const processadoModalContent = processadoModalOpen && processadoModalItem ? (
    createPortal(
      <div
        className="engenharia-homologacao-modal-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) closeModalProcessado()
        }}
        style={{ zIndex: 1000003 }}
      >
        <div className="engenharia-homologacao-modal" onClick={(e) => e.stopPropagation()}>
          <div className="engenharia-homologacao-modal-header">
            <div>
              <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>Marcar como processado</h3>
              <p style={{ margin: '6px 0 0', opacity: 0.9, fontSize: 13 }}>
                Cliente: {processadoModalItem?.cliente?.nome_completo || '—'}
              </p>
            </div>
            <button
              type="button"
              className="engenharia-homologacao-modal-close"
              onClick={closeModalProcessado}
              title="Fechar"
            >
              ×
            </button>
          </div>

          <div className="engenharia-homologacao-form">
            <div className="engenharia-homologacao-field">
              <label>Protocolo da Solicitação (apenas números)</label>
              <input
                inputMode="numeric"
                value={processadoProtocoloSolicitacao}
                onChange={(e) => setProcessadoProtocoloSolicitacao(parseToDigits(e.target.value))}
                placeholder="Ex.: 987654"
              />
              <small style={{ fontSize: 12, color: '#6b7280' }}>
                Obrigatório para marcar como processado.
              </small>
            </div>

            <div className="engenharia-homologacao-field">
              <label>Data e hora do processamento (data_processado)</label>
              <input
                type="datetime-local"
                value={processadoDataInput}
                onChange={(e) => setProcessadoDataInput(e.target.value)}
              />
              <small style={{ fontSize: 12, color: '#6b7280' }}>
                Campo iniciado com a data/hora atual, podendo ser ajustado.
              </small>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 10 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeModalProcessado}
                disabled={savingProcessado}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmarProcessado}
                disabled={savingProcessado}
              >
                {savingProcessado ? 'Salvando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    )
  ) : null

  const homologModalContent = homologadoModalCliente ? (
    createPortal(
      <div
        className="engenharia-homologacao-modal-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) fecharModalHomologadoCliente()
        }}
        style={{ zIndex: 1000005 }}
      >
        <div
          className="engenharia-homologacao-modal engenharia-homologacao-modal-homolog-cliente"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="eh-homolog-titulo"
        >
          <div className="engenharia-homologacao-modal-header">
            <div>
              <h3 id="eh-homolog-titulo" style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700 }}>
                Homologação do cliente
              </h3>
              <p style={{ margin: '8px 0 0', fontSize: 14 }}>{homologadoModalCliente.nome_completo || '—'}</p>
              <p style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.92 }}>
                CPF: {parseToDigits(homologadoModalCliente.cpf) || '—'}
              </p>
            </div>
            <button
              type="button"
              className="engenharia-homologacao-modal-close"
              onClick={fecharModalHomologadoCliente}
              title="Fechar"
            >
              ×
            </button>
          </div>
          <div className="engenharia-homologacao-form">
            <div className="form-check mb-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="eh-homologado-check"
                checked={homologadoDraft}
                onChange={(e) => setHomologadoDraft(e.target.checked)}
              />
              <label className="form-check-label fw-semibold" htmlFor="eh-homologado-check">
                Marcar como homologado
              </label>
            </div>
            {homologadoModalCliente.data_homologado ? (
              <p className="small text-muted mb-2">
                Última data registrada: {formatDateTime(homologadoModalCliente.data_homologado)}
              </p>
            ) : null}
            <p className="small text-muted mb-3">
              Ao salvar com homologado, a data e hora atuais serão gravadas em <strong>data_homologado</strong>. Ao
              desmarcar, a data é removida.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={fecharModalHomologadoCliente}
                disabled={savingHomologado}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={salvarHomologadoCliente}
                disabled={savingHomologado}
              >
                {savingHomologado ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    )
  ) : null

  const detalheModalContent = detalheModal ? (
    createPortal(
      <div
        className="engenharia-homologacao-modal-overlay engenharia-homologacao-modal-overlay--detalhe"
        onClick={(e) => {
          if (e.target === e.currentTarget) fecharDetalheModal()
        }}
        style={{ zIndex: 1000004 }}
      >
        <div
          className="engenharia-homologacao-modal engenharia-homologacao-modal-detalhe"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="eh-detalhe-titulo"
        >
          <div className="engenharia-homologacao-modal-header engenharia-homologacao-modal-header--detalhe">
            <div className="engenharia-homologacao-modal-header-text">
              <h3 id="eh-detalhe-titulo" className="engenharia-homologacao-detalhe-titulo">
                {tiposMap[detalheModal.tipoProcesso]?.label || detalheModal.tipoProcesso}
              </h3>
              <p className="engenharia-homologacao-detalhe-cliente-nome">{detalheModal.cliente?.nome_completo || '—'}</p>
              <p className="engenharia-homologacao-detalhe-cliente-meta">
                CPF: {parseToDigits(detalheModal.cliente?.cpf) || '—'}
              </p>
            </div>
            <button type="button" className="engenharia-homologacao-modal-close" onClick={fecharDetalheModal} title="Fechar">
              ×
            </button>
          </div>

          <div className="engenharia-homologacao-detalhe-scroll">
            <div className="engenharia-homologacao-form engenharia-homologacao-form--detalhe">
              {!detalheModal.item ? (
                <>
                  <p className="engenharia-homologacao-detalhe-lead">
                    Ainda não existe registro deste processo para este cliente. Informe os protocolos (opcional) e cadastre.
                  </p>
                  <div className="engenharia-homologacao-detalhe-grid-1">
                    <div className="engenharia-homologacao-field">
                      <label htmlFor="eh-detalhe-proto-site">Protocolo do Site (opcional)</label>
                      <input
                        id="eh-detalhe-proto-site"
                        inputMode="numeric"
                        autoComplete="off"
                        value={detalheProtoSite}
                        onChange={(e) => setDetalheProtoSite(parseToDigits(e.target.value))}
                        placeholder="Apenas números"
                      />
                    </div>
                    <div className="engenharia-homologacao-field">
                      <label htmlFor="eh-detalhe-proto-solic">Protocolo da Solicitação (opcional)</label>
                      <input
                        id="eh-detalhe-proto-solic"
                        inputMode="numeric"
                        autoComplete="off"
                        value={detalheProtoSolic}
                        onChange={(e) => setDetalheProtoSolic(parseToDigits(e.target.value))}
                        placeholder="Apenas números"
                      />
                    </div>
                  </div>
                  <div className="engenharia-homologacao-detalhe-actions">
                    <button type="button" className="btn btn-secondary" onClick={fecharDetalheModal} disabled={savingDetalhe}>
                      Cancelar
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleCriarDesdeDetalhe} disabled={savingDetalhe}>
                      {savingDetalhe ? 'Salvando...' : 'Cadastrar processo'}
                    </button>
                  </div>
                </>
              ) : (
                (() => {
                  const item = detalheModal.item
                  const stLabel = STATUS_PROCESSO.find(s => s.value === item.status)?.label || '—'
                  return (
                    <>
                      <section className="engenharia-homologacao-detalhe-section" aria-label="Datas e protocolos">
                        <h4 className="engenharia-homologacao-detalhe-section-title">Datas e protocolos</h4>
                        <dl className="engenharia-homologacao-detalhe-kv">
                          <div className="engenharia-homologacao-detalhe-kv-row">
                            <dt>{detalheModal.tipoProcesso === 'aumento_carga' ? 'Data de entrada' : 'Data da solicitação'}</dt>
                            <dd>{item.data_solicitacao ? formatDateTime(item.data_solicitacao) : '—'}</dd>
                          </div>
                          <div className="engenharia-homologacao-detalhe-kv-row">
                            <dt>Protocolo do Site</dt>
                            <dd className="engenharia-homologacao-protocolo">{parseToDigits(item.protocolo_site) || '—'}</dd>
                          </div>
                          <div className="engenharia-homologacao-detalhe-kv-row">
                            <dt>Protocolo da Solicitação</dt>
                            <dd className="engenharia-homologacao-protocolo">{parseToDigits(item.protocolo_solicitacao) || '—'}</dd>
                          </div>
                        </dl>
                      </section>

                      <section className="engenharia-homologacao-detalhe-section" aria-label="Status">
                        <h4 className="engenharia-homologacao-detalhe-section-title">Status</h4>
                        <div className="engenharia-homologacao-detalhe-status">
                          <span className={statusBadgeClass(item.status)}>{stLabel}</span>
                          <button
                            type="button"
                            className="btn btn-outline-primary engenharia-homologacao-detalhe-btn-status"
                            onClick={() => {
                              const it = detalheModal.item
                              fecharDetalheModal()
                              abrirModalStatus(it)
                            }}
                          >
                            Editar status
                          </button>
                        </div>
                        <dl className="engenharia-homologacao-detalhe-kv engenharia-homologacao-detalhe-kv--tight">
                          <div className="engenharia-homologacao-detalhe-kv-row">
                            <dt>Data do status</dt>
                            <dd>{item.data_status ? formatDateTime(item.data_status) : '—'}</dd>
                          </div>
                        </dl>
                        {item.status === 'reprovado' && item.status_motivo && (
                          <div className="engenharia-homologacao-detalhe-alert">
                            <strong>Motivo da reprovação</strong>
                            <p>{item.status_motivo}</p>
                          </div>
                        )}
                      </section>

                      <section className="engenharia-homologacao-detalhe-section" aria-label="Processamento">
                        <h4 className="engenharia-homologacao-detalhe-section-title">Processamento</h4>
                        <label className="engenharia-homologacao-detalhe-processado">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={!!item.processado}
                            onChange={(e) => {
                              const next = e.target.checked
                              if (next) abrirModalProcessado(item)
                              else handleSetProcessado(item, false)
                            }}
                            disabled={loading}
                          />
                          <span className="engenharia-homologacao-detalhe-processado-text">
                            <span className="engenharia-homologacao-detalhe-processado-label">Marcar como processado</span>
                            <span className="engenharia-homologacao-detalhe-processado-meta">
                              {item.processado && item.data_processado
                                ? formatDateTime(item.data_processado)
                                : 'Ainda não processado'}
                            </span>
                          </span>
                        </label>
                      </section>

                      <div className="engenharia-homologacao-detalhe-actions engenharia-homologacao-detalhe-actions--single">
                        <button type="button" className="btn btn-secondary" onClick={fecharDetalheModal}>
                          Fechar
                        </button>
                      </div>
                    </>
                  )
                })()
              )}
            </div>
          </div>
        </div>
      </div>,
      document.body
    )
  ) : null

  return (
    <div className="engenharia-homologacao-container">
      <div className="engenharia-homologacao-card">
        <div className="engenharia-homologacao-header">
          <div className="row w-100 g-2 align-items-start">
            <div className="col-12 col-md">
              <div className="d-flex align-items-start gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary engenharia-homologacao-btn-voltar"
                  onClick={onBack}
                  style={{ margin: 0, whiteSpace: 'nowrap' }}
                >
                  ← Voltar
                </button>

                <div className="engenharia-homologacao-header-titles text-center flex-grow-1">
                  <h2 className="engenharia-homologacao-title">🏗️ Homologação</h2>
                  <p className="engenharia-homologacao-subtitle">Visão em tabela por cliente (registros em engenharia_homologacao por tipo de processo)</p>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-auto d-grid gap-2 d-md-flex flex-md-nowrap">
              <button
                type="button"
                className="btn btn-outline-primary w-100 w-md-auto engenharia-homologacao-btn-icon-text"
                onClick={handleAtualizar}
                disabled={loading}
                title="Recarregar lista e clientes"
              >
                <span className="engenharia-homologacao-btn-icon-text-inner" aria-hidden>
                  {loading ? '⏳' : '🔄'}
                </span>
                <span>{loading ? 'Atualizando...' : 'Atualizar'}</span>
              </button>
              <button
                type="button"
                className="btn btn-outline-success w-100 w-md-auto"
                onClick={exportarParaExcel}
                disabled={loading || linhasTabelaOrdenadas.length === 0}
                title={
                  linhasTabelaOrdenadas.length === 0
                    ? 'Não há dados para exportar com os filtros atuais'
                    : 'Baixar planilha Excel com a mesma visão e filtros da tabela'
                }
              >
                📊 Exportar Excel
              </button>
              <button
                type="button"
                className="btn btn-primary w-100 w-md-auto"
                onClick={() => setShowNovoModal(true)}
              >
                ➕ Novo
              </button>
            </div>
          </div>
        </div>

        <div className="engenharia-homologacao-filtros">
          <div className="engenharia-homologacao-filtros-inner">
            <div className="engenharia-homologacao-filtros-linha1">
              <div className="engenharia-homologacao-filtro-campo">
                <label className="engenharia-homologacao-filtro-label" htmlFor="eh-filtro-cliente">
                  Nome do cliente
                </label>
                <input
                  id="eh-filtro-cliente"
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nome ou CPF..."
                  value={clienteSearch}
                  onChange={(e) => setClienteSearch(e.target.value)}
                />
              </div>
              <div className="engenharia-homologacao-filtro-campo">
                <label className="engenharia-homologacao-filtro-label" htmlFor="eh-filtro-tipo">
                  Tipo do Processo
                </label>
                <select
                  id="eh-filtro-tipo"
                  className="form-control"
                  value={tipoFilter}
                  onChange={(e) => setTipoFilter(e.target.value)}
                >
                  <option value="">Todos</option>
                  {TIPOS_PROCESSO.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="engenharia-homologacao-filtros-linha2">
              <div className="engenharia-homologacao-filtro-campo engenharia-homologacao-filtro-empresa">
                <label className="engenharia-homologacao-filtro-label" htmlFor="eh-filtro-empresa">
                  Empresa
                </label>
                {podeEscolherEmpresaFiltro ? (
                  <select
                    id="eh-filtro-empresa"
                    className="form-control"
                    value={filtroEmpresaId}
                    onChange={(e) => setFiltroEmpresaId(e.target.value)}
                  >
                    <option value="">Todas as empresas</option>
                    {listaEmpresasFiltro.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.empresa || emp.razao_social || emp.id}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="eh-filtro-empresa"
                    type="text"
                    className="form-control"
                    readOnly
                    disabled
                    value={userEmpresa?.empresa || '—'}
                    title="Empresa vinculada ao seu usuário"
                  />
                )}
              </div>

              <div className="engenharia-homologacao-filtro-campo engenharia-homologacao-filtro-protocolo-only">
                <label className="engenharia-homologacao-filtro-label" htmlFor="eh-filtro-protocolo">
                  Protocolo (Site ou Solicitação)
                </label>
                <input
                  id="eh-filtro-protocolo"
                  type="text"
                  className="form-control"
                  placeholder="Apenas números..."
                  value={protocoloFilter}
                  onChange={(e) => setProtocoloFilter(parseToDigits(e.target.value))}
                />
              </div>

              <div className="engenharia-homologacao-filtro-campo engenharia-homologacao-filtro-homolog-col">
                <span className="engenharia-homologacao-filtro-label" id="eh-filtro-homolog-legend">
                  Homologado
                </span>
                <div className="form-check engenharia-homologacao-filtro-homolog-check mb-0 engenharia-homologacao-filtro-homolog-valor">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="eh-filtro-homolog"
                    checked={filtroHomologadosSim}
                    onChange={(e) => setFiltroHomologadosSim(e.target.checked)}
                    aria-labelledby="eh-filtro-homolog-legend"
                    title="Marcado: apenas homologados. Desmarcado: apenas não homologados."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="engenharia-homologacao-empty">Carregando...</div>
        ) : linhasTabelaOrdenadas.length === 0 ? (
          <div className="engenharia-homologacao-empty">Nenhum registro encontrado.</div>
        ) : (
          <div className="engenharia-homologacao-table-wrapper table-responsive">
            <table className="engenharia-homologacao-table engenharia-homologacao-matrix-table">
              <thead>
                <tr>
                  <th>Nome do cliente</th>
                  <th
                    className="engenharia-homologacao-th-dias"
                    aria-sort={ordenacaoDiasColuna === 'desc' ? 'descending' : 'ascending'}
                  >
                    <button
                      type="button"
                      className="engenharia-homologacao-th-dias-btn"
                      onClick={alternarOrdenacaoDias}
                      title={
                        ordenacaoDiasColuna === 'desc'
                          ? 'Mais antigo para o mais novo (maior tempo em processo primeiro). Clique para inverter.'
                          : 'Mais novo para o mais antigo (menor tempo em processo primeiro). Clique para inverter.'
                      }
                    >
                      <span>Dias em processo</span>
                      <span className="engenharia-homologacao-th-dias-seta" aria-hidden>
                        {ordenacaoDiasColuna === 'desc' ? '▲' : '▼'}
                      </span>
                    </button>
                  </th>
                  <th>Aumento de carga</th>
                  <th>Troca de titularidade</th>
                  <th>Parecer de acesso</th>
                  <th>Vistoria</th>
                </tr>
              </thead>
              <tbody>
                {linhasTabelaOrdenadas.map((row) => {
                  const { cliente, porTipo } = row
                  const dc = cliente?.data_cadastro
                  const diasProc = diasDesdeCadastroCliente(dc)
                  const aumento = porTipo.aumento_carga
                  const troca = porTipo.troca_titularidade

                  return (
                    <tr key={row.clienteId}>
                      <td>
                        <button
                          type="button"
                          className="engenharia-homologacao-nome-cliente-btn"
                          onClick={() => abrirModalHomologadoCliente(cliente)}
                          title="Homologação do cliente (clique)"
                        >
                          <div className="fw-semibold text-nowrap">{cliente?.nome_completo || '—'}</div>
                          <div className="small text-muted">CPF: {parseToDigits(cliente?.cpf) || '—'}</div>
                        </button>
                      </td>
                      <td>
                        {diasProc == null ? (
                          <span className="engenharia-homologacao-muted">—</span>
                        ) : (
                          <span className="fw-semibold">{diasProc} {diasProc === 1 ? 'dia' : 'dias'}</span>
                        )}
                        <div className="engenharia-homologacao-cell-proc-meta">
                          Desde cadastro: {dc ? formatDateTime(dc) : '—'}
                        </div>
                      </td>
                      <MatrixProcessoCheckboxCell
                        cliente={cliente}
                        tipoProcesso="aumento_carga"
                        item={aumento}
                        onOpen={abrirDetalheProcesso}
                      />
                      <MatrixProcessoCheckboxCell
                        cliente={cliente}
                        tipoProcesso="troca_titularidade"
                        item={troca}
                        onOpen={abrirDetalheProcesso}
                      />
                      <MatrixProcessoCheckboxCell
                        cliente={cliente}
                        tipoProcesso="parecer_acesso"
                        item={porTipo.parecer_acesso}
                        onOpen={abrirDetalheProcesso}
                      />
                      <MatrixProcessoCheckboxCell
                        cliente={cliente}
                        tipoProcesso="vistoria_conexao"
                        item={porTipo.vistoria_conexao}
                        onOpen={abrirDetalheProcesso}
                      />
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalContent}
      {homologModalContent}
      {detalheModalContent}
      {statusModalContent}
      {processadoModalContent}

      {msgModal.show && (
        <ConfirmModal
          show={true}
          title={msgModal.title}
          message={msgModal.text}
          type={msgModal.type}
          showCancelButton={false}
          confirmText="OK"
          onConfirm={closeMsgModal}
          onCancel={closeMsgModal}
          zIndex={1000001}
        />
      )}
    </div>
  )
}

